import { useMemo, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Button, Progress, useDisclosure, addToast } from '@heroui/react';
import { IAssignmentTurnitinData } from '@/entities/Assignment';
import { TurnitinSubmissionStatus, useLazyGetTurnitinViewerUrlQuery } from '@/entities/Turnitin';
import { Svg } from '@/shared/ui/Svg';
import TurnitinProgressIcon from '@/shared/assets/icons/turnitin-progress.svg';
import { TurnitinViewerModal } from '../TurnitinViewerModal';
import { TurnitinEulaModal } from '../TurnitinEulaModal';
import { useTurnitinEulaCheck } from '../hooks/useTurnitinEulaCheck';

interface ITurnitinAntiplagiarismProps {
  data: IAssignmentTurnitinData;
  submissionId: number;
}

const COLORS_MAP = {
  'No Matches': '#B1CF55',
  Matches: '#EC6F5D',
  Internet: '#A2BCDD',
  Publications: '#FF723A', // оранжевый вместо зеленого
  'Submitted Works': '#FFD84B',
};

export const TurnitinAntiplagiarism = ({ data, submissionId }: ITurnitinAntiplagiarismProps) => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const {
    isEulaModalOpen,
    isAccepting,
    isChecking,
    eulaHtmlContent,
    checkBeforeSubmit,
    closeEulaModal,
    handleEulaLoaded,
    handleEulaAccept,
  } = useTurnitinEulaCheck();
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const status = data.status as TurnitinSubmissionStatus;
  const similarityStatus = data.similarityStatus as TurnitinSubmissionStatus;
  const isProcessing =
    status === TurnitinSubmissionStatus.PROCESSING ||
    similarityStatus === TurnitinSubmissionStatus.PROCESSING;
  const isComplete =
    status === TurnitinSubmissionStatus.COMPLETE &&
    similarityStatus === TurnitinSubmissionStatus.COMPLETE;
  const isError =
    status === TurnitinSubmissionStatus.ERROR ||
    similarityStatus === TurnitinSubmissionStatus.ERROR;

  const [
    getTurnitinViewerUrl,
    { isLoading: isGettingTurnitinViewerUrl, isFetching: isFetchingTurnitinViewerUrl },
  ] = useLazyGetTurnitinViewerUrlQuery();

  const performOpenViewer = async () => {
    try {
      const result = await getTurnitinViewerUrl(submissionId).unwrap();
      setViewerUrl(result.viewerUrl);
      onOpen();
    } catch (error: any) {
      if (error?.data?.message?.includes('EULA') || error?.data?.message?.includes('Turnitin')) {
        addToast({
          title: 'Turnitin EULA verification failed. Please try again.',
          color: 'danger',
        });
      } else {
        addToast({
          title: error?.data?.message || 'Failed to load Turnitin viewer',
          color: 'danger',
        });
      }
    }
  };

  const handleCheckInTurnitin = async () => {
    // Проверяем EULA перед открытием viewer
    const canProceed = await checkBeforeSubmit({} as any, async () => {
      await performOpenViewer();
    });

    if (canProceed) {
      // EULA уже принят, открываем viewer сразу
      await performOpenViewer();
    }
    // Если !canProceed - модалка EULA показана, viewer откроется после принятия
  };

  const chartData = useMemo(() => {
    if (!isComplete) return [];

    const matchesPercentage = data.matchesPercentage ?? 0;
    const noMatchesPercentage = 100 - matchesPercentage;

    const dataItems = [
      { name: 'No Matches', value: noMatchesPercentage, color: COLORS_MAP['No Matches'] },
      { name: 'Internet', value: data.internetPercentage ?? 0, color: COLORS_MAP.Internet },
      {
        name: 'Publications',
        value: data.publicationPercentage ?? 0,
        color: COLORS_MAP.Publications,
      },
      {
        name: 'Submitted Works',
        value: data.submittedWorksPercentage ?? 0,
        color: COLORS_MAP['Submitted Works'],
      },
    ].filter((item) => item.value !== null && item.value !== undefined && item.value > 0);

    return dataItems;
  }, [
    isComplete,
    data.matchesPercentage,
    data.internetPercentage,
    data.publicationPercentage,
    data.submittedWorksPercentage,
  ]);

  const percentageItems = useMemo(() => {
    if (!isComplete) return [];

    const matchesPercentage = data.matchesPercentage ?? 0;

    const items = [
      {
        label: 'Total Matches',
        value: matchesPercentage,
        color: COLORS_MAP['Matches'],
      },
      {
        label: 'Internet',
        value: data.internetPercentage,
        color: COLORS_MAP.Internet,
      },
      {
        label: 'Publications',
        value: data.publicationPercentage,
        color: COLORS_MAP.Publications,
      },
      {
        label: 'Submitted Works',
        value: data.submittedWorksPercentage,
        color: COLORS_MAP['Submitted Works'],
      },
    ].filter((item) => item.value !== null && item.value !== undefined);

    return items;
  }, [
    isComplete,
    data.matchesPercentage,
    data.internetPercentage,
    data.publicationPercentage,
    data.submittedWorksPercentage,
  ]);

  return (
    <>
      <section className="rounded-[24px] p-6 bg-white shadow-[0px_3px_12px_0px_#0000000D]">
        <h3 className="font-bold text-[24px] leading-[120%] tracking-0 mb-4">
          Antiplagiarism Status
        </h3>
        <div className="flex flex-col">
          {isError ? (
            <div className="flex flex-col gap-3">
              {data.statusErrorMessage && (
                <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-sm font-medium text-red-600">Status Error</p>
                  <p className="text-sm text-red-700 mt-1">{data.statusErrorMessage}</p>
                </div>
              )}
              {data.statusSimilarityErrorMessage && (
                <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-sm font-medium text-red-600">Similarity Status Error</p>
                  <p className="text-sm text-red-700 mt-1">{data.statusSimilarityErrorMessage}</p>
                </div>
              )}
              {!data.statusErrorMessage && !data.statusSimilarityErrorMessage && (
                <p className="text-sm text-red-600">An error occurred during plagiarism check</p>
              )}
            </div>
          ) : isProcessing ? (
            <div className="flex flex-col items-center justify-center gap-4 py-8">
              <Svg Icon={TurnitinProgressIcon} className="size-9" />
              <p className="rounded-[10px] p-[10px] bg-yellow-secondary text-sm">
                The assignment is currently being checked for plagiarism. Please wait until the
                check is complete.
              </p>
            </div>
          ) : isComplete ? (
            <div className="flex gap-4">
              <div className="flex flex-col gap-6">
                {/*
                  Диаграмма дублирует то, что рядом написано текстом: доля и её значение
                  есть в списке справа. Recharts сама ставит слою tabindex="0", и в обходе
                  появлялась безымянная остановка. Убираем её из табуляции и от скринридера.
                */}
                <div
                  aria-hidden="true"
                  className="flex justify-center items-center gap-4 min-w-[180px] min-h-[180px]"
                >
                  <ResponsiveContainer width="100%" height="100%" minWidth={180} minHeight={180}>
                    <PieChart width={170} height={170}>
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0];
                            return (
                              <div className="bg-white border border-grey-border rounded-lg p-2 shadow-lg">
                                <p className="text-sm font-bold">{data.name}</p>
                                <p className="text-sm text-grey-secondary">{data.value}%</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Pie
                        rootTabIndex={-1}
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={85}
                        paddingAngle={0}
                        dataKey="value"
                      >
                        {chartData.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <Button
                  variant="bordered"
                  onPress={handleCheckInTurnitin}
                  isLoading={
                    isGettingTurnitinViewerUrl || isFetchingTurnitinViewerUrl || isChecking
                  }
                  className="border-[#211D55] text-[#211D55] hover:bg-[#211D55] hover:text-white"
                >
                  Check in Turnitin
                </Button>
              </div>
              <div
                className="grid gap-4 items-start"
                style={{ gridTemplateColumns: '55% 45%', width: 'calc(100% - 170px - 16px)' }}
              >
                <div className="grid grid-cols-2 gap-4 items-start">
                  {percentageItems.map((item) => (
                    <div key={item.label} className="flex flex-col gap-2">
                      <div className="flex flex-col gap-1 text-sm">
                        <span className="text-sm text-grey-tertiary leading-[1.2] tracking-0">
                          {item.label}
                        </span>
                        <span className="text-sm font-medium leading-[1.4] tracking-[2%]">
                          {item.value}%
                        </span>
                      </div>
                      <div
                        className="w-full p-1 rounded-[200px] border-[2px]"
                        style={
                          {
                            borderColor: item.color,
                            '--progress-color': item.color,
                          } as React.CSSProperties
                        }
                      >
                        <Progress
                          value={item.value ?? 0}
                          maxValue={100}
                          size="md"
                          aria-label={`${item.label} percentage`}
                          classNames={{
                            indicator: 'transition-all duration-300 bg-[var(--progress-color)]',
                            track: 'bg-transparent',
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <ul className="space-y-[10px]">
                  <li>
                    <p className="text-[14px] leading-[1.4] tracking-0">
                      <span className="font-bold">Matches -</span> fragments of the checked text,
                      completely or partially similar to the sources found, excluding fragments that
                      are related to citation or self-citation. The Matches indicator shows for
                      fragments of the checked text, related to matches, in the total text volume.
                    </p>
                  </li>
                  <li>
                    <p className="text-[14px] leading-[1.4] tracking-0">
                      <span className="font-bold">Self-citation -</span> fragments of the checked
                      text, which are similar or almost similar to fragments of the text whose
                      author is the author of the checked document. The Self-citation indicator
                      shows for fragments of the text, related to self-citation, in the total text
                      volume.
                    </p>
                  </li>
                  <li>
                    <p className="text-[14px] leading-[1.4] tracking-0">
                      <span className="font-bold">Citation -</span> fragments of the checked text
                      that are not author&apos;s, for which the system considers citation or
                      references. The Citation indicator shows for fragments of the checked text,
                      related to citations, in the total text volume.
                    </p>
                  </li>
                  <li>
                    <p className="text-[14px] leading-[1.4] tracking-0">
                      <span className="font-bold">Original text -</span> fragments of the checked
                      text, not found in any sources, that do not match other documents in the
                      system.
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          ) : null}
        </div>
      </section>
      {isOpen && (
        <TurnitinViewerModal
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          viewerUrl={viewerUrl}
          isLoading={isGettingTurnitinViewerUrl}
        />
      )}
      {isEulaModalOpen && eulaHtmlContent && (
        <TurnitinEulaModal
          isOpen={isEulaModalOpen}
          isLoading={isAccepting}
          htmlContent={eulaHtmlContent}
          onClose={closeEulaModal}
          onLoaded={handleEulaLoaded}
          onAccept={handleEulaAccept}
        />
      )}
    </>
  );
};
